import { IVec2, Vec2 } from "./vec2";


export interface IRotatedRect {
	center: IVec2;
	size: IVec2;
	rotation: number; // Clockwise in degrees
}


export const isInsideRotatedRect = (rect: IRotatedRect, point: IVec2): boolean => {
	let rotatedPoint = Vec2.rotateAroundPoint(point, -rect.rotation, rect.center);
	let halfSize = { x: Math.abs(rect.size.x) * 0.5, y: Math.abs(rect.size.y) * 0.5 };
	if (rotatedPoint.x < rect.center.x - halfSize.x) return false;
	if (rotatedPoint.x > rect.center.x + halfSize.x) return false;
	if (rotatedPoint.y < rect.center.y - halfSize.y) return false;
	if (rotatedPoint.y > rect.center.y + halfSize.y) return false;
	return true;
};

export const getAxisAlignedBoundingSize = (rect: IRotatedRect): IVec2 => {
	if (rect.rotation === 0) {
		return {...rect.size};
	}

	const rotatedCorner1 = Vec2.rotate(rect.size, rect.rotation);
	const rotatedCorner2 = Vec2.rotate({ x: rect.size.x, y: -rect.size.y }, rect.rotation);

	return { 
		x: Math.max(Math.abs(rotatedCorner1.x), Math.abs(rotatedCorner2.x)), 
		y: Math.max(Math.abs(rotatedCorner1.y), Math.abs(rotatedCorner2.y)),
	};
}

export const lerp = (a: number, b: number, t: number): number => {
	return (1 - t) * a + t * b;
};

export const inverseLerp = (a: number, b: number, value: number): number => {
	return (value - a) / (b - a);
};

export const lineSegmentsIntersection = (a1: IVec2, a2: IVec2, b1: IVec2, b2: IVec2) => {
	const uA = ((b2.x-b1.x)*(a1.y-b1.y) - (b2.y-b1.y)*(a1.x-b1.x)) / ((b2.y-b1.y)*(a2.x-a1.x) - (b2.x-b1.x)*(a2.y-a1.y));
	const uB = ((a2.x-a1.x)*(a1.y-b1.y) - (a2.y-a1.y)*(a1.x-b1.x)) / ((b2.y-b1.y)*(a2.x-a1.x) - (b2.x-b1.x)*(a2.y-a1.y));
	return uA >=0 && uA <= 1 && uB >= 0 && uB <= 1;
};

export const rectangleLineSegmentIntersection = (rect: IRotatedRect, lineStart: IVec2, lineEnd: IVec2): boolean => {
	console.assert(rect.size.x >= 0 && rect.size.y >= 0);
	
	// Move and rotate the line so that the rectangle is centered at the origin and axis aligned
	const p0 = Vec2.rotate(Vec2.sub(lineStart, rect.center), -rect.rotation);
	const p1 = Vec2.rotate(Vec2.sub(lineEnd, rect.center), -rect.rotation);

	// Quick checks if line is completely to one side of the rectangle
	const halfSize = { x: rect.size.x * 0.5, y: rect.size.y * 0.5 };
	if (p0.x < -halfSize.x && p1.x < -halfSize.x) return false;
	if (p0.x > halfSize.x && p1.x > halfSize.x) return false;
	if (p0.y < -halfSize.y && p1.y < -halfSize.y) return false;
	if (p0.y > halfSize.y && p1.y > halfSize.y) return false;

	// Check if the start point is inside the rectangle
	if (Math.abs(p0.x) < halfSize.x && Math.abs(p0.y) < halfSize.y) return true;

	// Check if there is an intersection with any of the sides of the rectangle
	if (lineSegmentsIntersection(p0, p1, {x: halfSize.x, y: halfSize.y}, {x: -halfSize.x, y: halfSize.y})) return true;
	if (lineSegmentsIntersection(p0, p1, {x: -halfSize.x, y: halfSize.y}, {x: -halfSize.x, y: -halfSize.y})) return true;
	if (lineSegmentsIntersection(p0, p1, {x: -halfSize.x, y: -halfSize.y}, {x: halfSize.x, y: -halfSize.y})) return true;
	if (lineSegmentsIntersection(p0, p1, {x: halfSize.x, y: -halfSize.y}, {x: halfSize.x, y: halfSize.y})) return true;

	return false;
};

export const triangleLineSegmentIntersection = (triangleRect: IRotatedRect, lineStart: IVec2, lineEnd: IVec2): boolean => {
	console.assert(triangleRect.size.x >= 0 && triangleRect.size.y >= 0);

	// Move and rotate the line so that the triangle rect is centered at the origin and axis aligned
	const p0 = Vec2.rotate(Vec2.sub(lineStart, triangleRect.center), -triangleRect.rotation);
	const p1 = Vec2.rotate(Vec2.sub(lineEnd, triangleRect.center), -triangleRect.rotation);

	// Quick checks if line is completely to one side of the triangle rect bounds
	const halfSize = { x: triangleRect.size.x * 0.5, y: triangleRect.size.y * 0.5 };
	if (p0.x < -halfSize.x && p1.x < -halfSize.x) return false;
	if (p0.x > halfSize.x && p1.x > halfSize.x) return false;
	if (p0.y < -halfSize.y && p1.y < -halfSize.y) return false;
	if (p0.y > halfSize.y && p1.y > halfSize.y) return false;

	// Check if there is an intersection with any of the sides of the triangle
	const a = { x: 0, y: halfSize.y };
	const b = { x: -halfSize.x, y: -halfSize.y };
	const c = { x: halfSize.x, y: -halfSize.y };
	if (lineSegmentsIntersection(p0, p1, a, b)) return true;
	if (lineSegmentsIntersection(p0, p1, b, c)) return true;
	if (lineSegmentsIntersection(p0, p1, c, a)) return true;

	// Check if the start point is inside the triangle
	const ab = Vec2.sub(b, a);
	const bc = Vec2.sub(c, b);
	const ca = Vec2.sub(a, c);
	const ap0 = Vec2.sub(p0, a);
	const bp0 = Vec2.sub(p0, b);
	const cp0 = Vec2.sub(p0, c);

	// Since size must be positive, points abc of the triangle are known to be
	// in counter-clockwise order. All these cross products will be positive if
	// p0 is in the triangle
	if (Vec2.cross(ab, ap0) < 0) return false;
	if (Vec2.cross(bc, bp0) < 0) return false;
	if (Vec2.cross(ca, cp0) < 0) return false;

	return true;
};

export const ovalLineSegmentIntersection = (ovalRect: IRotatedRect, lineStart: IVec2, lineEnd: IVec2): boolean => {
	console.assert(ovalRect.size.x >= 0 && ovalRect.size.y >= 0);
	
	// Move, rotate and scale the line so that the oval is a circle centered at the origin with radius 1
	const p0Scaled = Vec2.rotate(Vec2.sub(lineStart, ovalRect.center), -ovalRect.rotation);
	const p1Scaled = Vec2.rotate(Vec2.sub(lineEnd, ovalRect.center), -ovalRect.rotation);
	const p0 = { x: p0Scaled.x * 2 / ovalRect.size.x, y: p0Scaled.y * 2 / ovalRect.size.y };
	const p1 = { x: p1Scaled.x * 2 / ovalRect.size.x, y: p1Scaled.y * 2 / ovalRect.size.y }; 
	
	// Quick checks if line is completely to one side of the oval rect bounds
	if (p0.x < -1 && p1.x < -1) return false;
	if (p0.x > 1 && p1.x > 1) return false;
	if (p0.y < -1 && p1.y < -1) return false;
	if (p0.y > 1 && p1.y > 1) return false;

	// Check if the start point is inside the oval
	if (p0.x * p0.x + p1.x * p1.x < 1) return true;

	// Since the origin is the center of the circle, we need to find the point along the line that is closest 
	// to the origin, call it A. First we project -p0 onto p0p1, adding p0 to that will give us A. Finally
	// check if A is within the circle's radius.
	const lineDir = Vec2.normalize(Vec2.sub(p1, p0));
	const p0A = Vec2.scale(lineDir, -Vec2.dot(p0, lineDir)); // Projection
	const A = Vec2.add(p0, p0A);
	const distA = Vec2.sqrLength(A);

	return distA < 1;
};
